import type { RfidDocumentRow } from '../services/rfid.service';
import { documentHasKitStructure } from './kitDocument';

export { documentHasKitStructure, documentHasKitStructure as documentHasKitComponents } from './kitDocument';

export function formatDateEnGb(d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function normalizeItemArray(raw: unknown): Record<string, unknown>[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw as Record<string, unknown>[];
  return [raw as Record<string, unknown>];
}

export function getDocumentItems(doc: RfidDocumentRow): Record<string, unknown>[] {
  const items = doc.Items as { Item?: unknown } | undefined;
  return normalizeItemArray(items?.Item);
}

export function getDocumentPlant(doc: RfidDocumentRow): string {
  const header = String(doc.Sender ?? doc.Plant ?? '').trim();
  if (header) return header;
  const first = getDocumentItems(doc)[0];
  return String(first?.Sender ?? first?.Plant ?? '').trim();
}

/**
 * SAP `/FetchStorageLocation` expects a bare plant (e.g. `1005`), not BP-style `PL1005`.
 */
export function normalizeStorageLocationPlant(code: string): string {
  const plant = String(code ?? '').trim();
  if (!plant) return '';
  if (/^PL/i.test(plant) && plant.length > 2) return plant.slice(2).trim();
  return plant;
}

function relocationInPlant(doc: RfidDocumentRow): string {
  // Legacy RelocIn fetchBays uses items[0].TO_WH (destination WH), not Sender.
  const first = getDocumentItems(doc)[0];
  const toWh = String(
    first?.TO_WH ?? first?.ToWH ?? first?.To_WH ?? first?.TOWH ?? ''
  ).trim();
  if (toWh) return toWh;
  const receiver = String(doc.Receiver ?? '').trim();
  if (receiver) return receiver;
  return getDocumentPlant(doc);
}

/**
 * Plant code for `/FetchStorageLocation`.
 * Legacy LeapIn uses RECEIVER with the first two characters stripped (e.g. IN9000 → 9000).
 * Relocation In uses TO_WH (legacy) and must not send a PL… BP plant code.
 */
export function getStorageLocationPlant(
  doc: RfidDocumentRow,
  movementId?: string
): string {
  if (movementId === 'dehire_received') {
    const receiver = String(doc.Receiver ?? '').trim();
    if (receiver.length > 2) return normalizeStorageLocationPlant(receiver.slice(2));
    if (receiver) return normalizeStorageLocationPlant(receiver);
  }
  if (movementId === 'relocation_in') {
    return normalizeStorageLocationPlant(relocationInPlant(doc));
  }
  return normalizeStorageLocationPlant(getDocumentPlant(doc));
}

export function detectWizardVariant(doc: RfidDocumentRow): 'kit' | 'flat' {
  return documentHasKitStructure(doc) ? 'kit' : 'flat';
}
